import { useQuery } from '@apollo/client';
import { FETCH_CHART } from '../queries';
import { Spinner } from '../../../components';
import Chart from '../../../components/Chart';
import { useLocation, useSearchParams } from 'react-router-dom';
import { FETCH_LOCATION_CHART } from '../queries/RiskHub';
import { Tooltip } from 'antd';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import { trimName } from '../../../utils/trimName';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import { getStatusEmoji } from '../../../utils';

import QueryErrorBoundary from '../../../components/error-handling';

type Location = {
  locationName: string;
  uuid: string;
};

type ExposureByLocation = {
  location: Location;
  bcpStatus: string;
  mitigatedRevenue: number;
  unmitigatedRevenue: number;
};

interface DataResponse {
  exposureByLocation: ExposureByLocation[];
}

export type chartData = {
  label: string;
  type: 'Total Revenue Exposure' | 'Residual Revenue Exposure';
  value: number;
};

export function ExposuresChart() {
  let [searchParams] = useSearchParams();

  const { canViewFinancials, isLocation, selectedTags } = useUserContext();

  const location = useLocation();
  const divisionUuid = searchParams.get('division');
  const locationUuid = searchParams.get('location');

  const pathname = location.pathname;
  const IS_LOCATION_RISK_HUB = pathname === '/location-risk-hub';

  const query = IS_LOCATION_RISK_HUB ? FETCH_LOCATION_CHART : FETCH_CHART;

  const { data, loading, error } = useQuery(query, {
    variables: {
      filter: IS_LOCATION_RISK_HUB ? { locationUuid } : { divisionUuid },
    },
  });

  const riskHubData = IS_LOCATION_RISK_HUB
    ? data?.exposureByProcessArea
    : data?.exposureByLocation;

  if (loading)
    return (
      <div className="flex items-center justify-center p-6">
        <Spinner />
      </div>
    );

  const transformedData: chartData[] = riskHubData?.flatMap((exposure: any) => [
    {
      uuid: exposure.location?.uuid,
      label: `${
        IS_LOCATION_RISK_HUB
          ? trimName(exposure.processArea)
          : trimName(exposure.location.locationName)
      }     ${getStatusEmoji(exposure.bcpStatus)}`,
      type: 'Total Revenue Exposure',
      value: exposure.unmitigatedRevenue,
    },
    {
      uuid: exposure.location?.uuid,
      label: `${
        IS_LOCATION_RISK_HUB
          ? trimName(exposure.processArea)
          : trimName(exposure.location.locationName)
      }     ${getStatusEmoji(exposure.bcpStatus)}`,
      type: 'Residual Revenue Exposure',
      value: exposure.mitigatedRevenue,
    },
  ]);

  const filteredData = !IS_LOCATION_RISK_HUB
    ? getFilteredTags({
        data: transformedData,
        condition: isLocation,
        selectedTags,
      })
    : transformedData;

  return (
    <div className="w-full my-8 rounded-[10px] border border-secondary-100 overflow-hidden relative">
      <h2 className="px-6 py-2 text-base text-primary-100 tracking-wider font-medium border-b border-secondary-100 bg-silver flex items-center">
        {IS_LOCATION_RISK_HUB
          ? 'Exposures by Process Area'
          : 'Exposures by Location'}
      </h2>
      <QueryErrorBoundary error={error}>
        <div className="relative">
          {!canViewFinancials && (
            <div className="w-full h-full absolute top-0 left-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
              <p className="text-primary-100 text-lg font-semibold text-center">
                <Tooltip title="You do not have permission to see financials">
                  <EyeInvisibleOutlined />
                </Tooltip>
              </p>
            </div>
          )}
          <div className="p-2 mt-8 w-full flex  items-center justify-center">
            <div className="relative w-full">
              <h3
                className={`mb-3   font-bold text-xsm text-primary-100 tracking-wider absolute ${
                  IS_LOCATION_RISK_HUB ? 'left-32' : 'left-40'
                }`}
              >
                {IS_LOCATION_RISK_HUB ? 'PROCESS AREA' : 'LOCATION'}
              </h3>
              <h3 className="mb-3   font-bold text-xsm text-primary-100 tracking-wider absolute left-60">
                BCP STATUS
              </h3>
              <Chart data={filteredData} />
              <div className="w-full my-6 flex flex-nowrap justify-center items-center gap-10">
                <p className="text-sm ">
                  <span role="img" aria-label="grey">
                    ⚪️
                  </span>
                  <span className="font-light ml-2">Not Started</span>
                </p>
                <p className="text-sm">
                  <span role="img" aria-label="orange">
                    🟠
                  </span>
                  <span className="font-light ml-2">Out of Date</span>
                </p>
                <p className="text-sm">
                  <span role="img" aria-label="blue">
                    🔵
                  </span>
                  <span className="font-light ml-2">In Progress</span>
                </p>
                <p className="text-sm">
                  <span role="img" aria-label="green">
                    🟢
                  </span>
                  <span className="font-light ml-2">Finalized</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </QueryErrorBoundary>
    </div>
  );
}
